import { SystemCatalogCategory } from '@/queries/system_catalog/system_catalog.schema'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shad/ui/dialog'
import { selectSelectedProgram } from '@/storage/programs/slice'
import { useSelector } from 'react-redux'
import useEditRule from '../../hooks/useEditRule'
import {
  ApprovalRule,
  ApprovalRulePayload,
  PaymentRulesConfig,
} from '../../schema'
import CreateRuleForm, {
  RuleFormValues,
} from '../create-rule-form/create-rule-form'

interface EditRuleFormProps {
  rules: ApprovalRule[]
  onOpenChange: (open: boolean) => void
  onEditSuccess: () => void
  onCancel: () => void
  approvalRulesConfig: PaymentRulesConfig
  categories: SystemCatalogCategory[]
  selectedRuleId: number | null
}

export default function EditRuleForm({
  approvalRulesConfig,
  categories,
  selectedRuleId,
  rules,
  onOpenChange,
  onCancel,
  onEditSuccess,
}: EditRuleFormProps) {
  const selectedRule = selectedRuleId
    ? rules.find((rule) => rule.id === selectedRuleId)
    : null

  const { mutate: updateRule, status } = useEditRule({
    onSuccess: onEditSuccess,
  })
  const programId = useSelector(selectSelectedProgram).id

  // Early return if no rule is selected or rule doesn't exist
  if (!selectedRuleId || !selectedRule) {
    return null
  }

  // At this point, selectedRule is guaranteed to be non-null
  const rule = selectedRule

  const allCategories = categories.map((category) => {
    return {
      id: category.id,
      name: category.name,
    }
  })
  const items = categories.flatMap((category) =>
    category.items.map((item) => {
      return {
        id: item.id,
        name: item.name,
      }
    })
  )

  // Find the target ID based on the target name and type
  const getTargetId = (): string => {
    const targetType = rule.target_type_code
    const targetName = rule.target_name

    if (targetType === 'CATEGORY') {
      const category = allCategories.find((cat) => cat.name === targetName)
      return category?.id.toString() ?? ''
    }

    if (targetType === 'ITEM') {
      const item = items.find((item) => item.name === targetName)
      return item?.id.toString() ?? ''
    }

    if (targetType === 'PAYMENT_WORKFLOW') {
      const paymentType = approvalRulesConfig.global_payment_types.find(
        (pt) => pt.name === targetName
      )
      return paymentType?.id.toString() ?? ''
    }

    // Exhaustive check - this will cause a TypeScript error if any target type is missing
    const exhaustiveCheck: never = targetType
    throw new Error(`Invalid target type: ${exhaustiveCheck}`)
  }

  function handleEditRule(formData: RuleFormValues) {
    const rulePayload: ApprovalRulePayload = {
      name: formData.name,
      is_active: formData.is_active,
      target_id: formData.targetId,
      description: formData.description,
      program_id: programId,
      approval_type: formData.approvalType,
      target_type: formData.targetType,
    }

    updateRule({
      ruleId: rule.id,
      rule: rulePayload,
    })
  }

  return (
    <Dialog open={true} onOpenChange={onOpenChange}>
      <DialogContent
        onInteractOutside={(e) => {
          e.preventDefault()
        }}
        className="max-w-md"
      >
        <DialogHeader>
          <DialogTitle>Edit Rule</DialogTitle>
          <DialogDescription>
            Update the approval rule configuration
          </DialogDescription>
        </DialogHeader>

        <CreateRuleForm
          approvalRulesConfig={approvalRulesConfig}
          categories={allCategories}
          items={items}
          onSubmit={handleEditRule}
          onCancel={onCancel}
          createRuleStatus={status}
          defualtValues={{
            is_active: rule.is_active,
            targetType: rule.target_type_code,
            approvalType: rule.approval_type_code,
            description: rule?.description,
            name: rule.name,
            targetId: getTargetId(),
          }}
        />
      </DialogContent>
    </Dialog>
  )
}
